import { useEffect, useRef, useState } from 'react';
import { dateWithNoTimeDashed } from './date';
/**
 * Tracks a user-selectable date that is "anchored" to a moving reference point
 * (e.g. today, or the current week's Monday).
 *
 * When `anchor` advances to a new calendar day — for example because
 * {@link useToday} rolled the day over at midnight or on foreground — the
 * selected date is advanced too, but ONLY if the user was still sitting on the
 * previous anchor. If they had navigated back to an earlier date, their
 * selection is preserved.
 *
 * @param anchor the current reference date ("today"); should be stable in
 *   identity while the day is unchanged (memoize it if it is derived).
 */
export function useAnchoredDate(anchor) {
    const [date, setDate] = useState(anchor);
    const prevAnchorRef = useRef(anchor);
    useEffect(() => {
        const prevAnchor = prevAnchorRef.current;
        if (dateWithNoTimeDashed(prevAnchor) === dateWithNoTimeDashed(anchor)) {
            return;
        }
        prevAnchorRef.current = anchor;
        setDate(current => dateWithNoTimeDashed(current) === dateWithNoTimeDashed(prevAnchor)
            ? anchor
            : current);
    }, [anchor]);
    return [date, setDate];
}
